import { Component, DestroyRef, OnInit, ChangeDetectionStrategy, effect, inject, input, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AddAdditionalImageDialogComponent,
  AdditionalImageDialogInputData,
  getPrimaryImageOrFirst,
  isMediaImage,
  isMediaInfoPublishable,
  PropertyMediaInfo,
  MiqImagesModule,
  
} from '@miq/utils';
import { PACIFIC_TEMPLATES_IMAGE_ORDER } from '../../../../../lib/shared/templates/template-blueprints.const';
import { L10nTranslationService, L10nTranslationModule } from 'angular-l10n';
import { filter, switchMap } from 'rxjs/operators';
import { GroupBase } from '../../../../models/blueprint.model';
import { DataObject, ReportRecord, ReportView } from '../../../../models/report.model';
import { ReportAllRecordsImage, TemplateField } from '../../../../models/template.model';
import { ReportExportViewService } from '../../../../report-export-view.service';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from '../../../../report-export-view.token';
import { ReportImagesService } from '../../../../report-images.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
  ADDITIONAL_IMAGE_FIELD_ID,
  IMAGE_FIELD_ID,
  LAYOUT_IMAGE_3_ID,
} from '../../../../shared/templates/config/common';
import { ReportResetImagesService } from './report-reset-images.service';
import { AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { ReportFieldFormatterPipe } from '../../../../shared/pipes/report-field-formatter.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-presentation-dynamic-view-image',
  templateUrl: './record-image.component.html',
  styleUrl: './record-image.component.scss',
  imports: [MiqImagesModule, MatIcon, L10nTranslationModule, AsyncPipe, ReportFieldFormatterPipe],
})
export class PresentationDynamicViewImageComponent implements OnInit {
  readonly reportRecordData = input.required<ReportRecord>();
  readonly imageGroups = input.required<GroupBase<TemplateField>[]>();
  readonly currentView = input.required<ReportView>();
  readonly reportId = input.required<number>();

  /** Local mutable copy for reassignment (signal inputs are readonly) */
  reportRecordDataLocal: ReportRecord;

  public imageField: TemplateField;
  public additionalImageField: TemplateField;
  public secondaryImageField: TemplateField;
  public edpMultimediaURI: string;
  public s3BucketImagesUrl: string;
  public hasAdditionalImage = false;
  public hasSecondaryImage = false;
  public reportAllImages: ReportAllRecordsImage;
  private edpRecordId: number;

  private readonly reportModuleConfigs = inject<ReportExportConfigurations>(REPORT_EXPORT_CONFIGS_TOKEN);
  private readonly notificationService = inject(NotificationService);
  private readonly reportExportViewService = inject(ReportExportViewService);
  private readonly reportImagesService = inject(ReportImagesService);
  public translationService = inject(L10nTranslationService);
  private readonly reportResetImagesService = inject(ReportResetImagesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.edpMultimediaURI = this.reportModuleConfigs.edpMultimediaURI;
    this.s3BucketImagesUrl = this.reportModuleConfigs.s3BucketImagesUrl;

    effect(() => {
      const reportRecordData = this.reportRecordData();
      const imageGroups = this.imageGroups();
      this.reportRecordDataLocal = reportRecordData;
      if (reportRecordData != null) {
        this.edpRecordId = this.getEdpInfo();
        (imageGroups || []).forEach(imageGroup => {
          this.imageField = (imageGroup.fields || []).find(field => field.id === IMAGE_FIELD_ID);
          this.additionalImageField = (imageGroup.fields || []).find(field => field.id === ADDITIONAL_IMAGE_FIELD_ID);
          this.secondaryImageField = (imageGroup.fields || []).find(field => field.id === LAYOUT_IMAGE_3_ID);
        });
        this.showImages();
      }
    });
  }

  ngOnInit() {
    // used for translations
    this.reportImagesService.onUpdateAdditionalImage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.reportAllImages = response;
      this.showImages();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    // used for destroy the Subscription.
  }

  /**
   * method to add the additional image to the record under report.
   */
  public addImage() {
    const imagesToDisplay: PropertyMediaInfo[] = this.getImagesWithoutPrimary();
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId: null,
      translatedData,
    };
    let selectedImage: PropertyMediaInfo;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          return this.reportExportViewService.addAdditionalImage(this.reportId(), [
            { edpRecordId: this.edpRecordId, imageId: image.id },
          ]);
        })
      )
      .subscribe((reportImageId: number[]) => {
        const recordImages = this.reportAllImages[this.edpRecordId];
        if (recordImages != null) {
          recordImages.push({
            reportImageId: reportImageId[0],
            imageId: selectedImage.id,
          });
        } else {
          const newRecordImage = [
            {
              reportImageId: reportImageId[0],
              imageId: selectedImage.id,
            },
          ];
          this.reportAllImages[this.edpRecordId] = newRecordImage;
        }
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.cdr.markForCheck();
      });
  }

  /**
   * this method is to edit the 2,3 images
   * @param index is the index value either of secondary or additional image.
   */
  public editImage(index: number) {
    const selectedImageId = this.reportAllImages[this.edpRecordId][index].imageId;
    const imagesToDisplay: PropertyMediaInfo[] = this.getImagesWithoutPrimary();
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId,
      translatedData,
    };
    let selectedImage: PropertyMediaInfo;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          const reportImageId = this.reportAllImages[this.edpRecordId][index].reportImageId;
          return this.reportExportViewService.changeAdditionalImage(this.reportId(), reportImageId, image.id);
        })
      )
      .subscribe((reportImageId: number) => {
        this.reportAllImages[this.edpRecordId][index] = {
          reportImageId,
          imageId: selectedImage.id,
        };
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.reportRecordDataLocal = { ...this.reportRecordDataLocal };
        // the reason why again reassigning is to re generate the image which is not under the reportRecordData.
        // So when there is edit it was not calling
        this.cdr.markForCheck();
      });
  }

  /**
   * this method is to delete the 2,3 images.
   * @param index is the index value of image either of additional or secondary images.
   */
  public deleteImage(index: number) {
    if (this.reportAllImages && Object.keys(this.reportAllImages).length > 0) {
      const reportImageId = this.reportAllImages[this.edpRecordId][index].reportImageId;
      this.reportExportViewService.deleteAdditionalImage(this.reportId(), reportImageId).subscribe(() => {
        this.reportAllImages[this.edpRecordId].splice(index, 1);
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
        this.reportRecordDataLocal = { ...this.reportRecordDataLocal };
        // the reason why again reassigning is to re generate the image which is not under the reportRecordData.
        // So when there is edit it was not calling
        this.cdr.markForCheck();
      });
    } else {
      this.hasAdditionalImage = false;
      this.hasSecondaryImage = false;
    }
  }

  /**
   * this method is used to fetch the images without the primary image.
   */
  private getImagesWithoutPrimary() {
    let imagesToDisplay: PropertyMediaInfo[] = [];
    const mediaInfo = (this.reportRecordDataLocal.associatedProperty as DataObject).getMediaInformation();
    if (mediaInfo.length) {
      const primaryImage = getPrimaryImageOrFirst(mediaInfo);
      if (primaryImage) {
        imagesToDisplay = mediaInfo.filter(
          (media: PropertyMediaInfo) =>
            media.id !== primaryImage.id && isMediaImage(media) && isMediaInfoPublishable(media)
        );
      }
    }
    return imagesToDisplay;
  }

  private showImages() {
    if (
      this.reportAllImages &&
      Object.keys(this.reportAllImages).length > 0 &&
      this.reportAllImages[this.edpRecordId]
    ) {
      const [additionalImage, secondaryImage] = this.reportAllImages[this.edpRecordId];
      this.hasAdditionalImage = !!additionalImage;
      this.hasSecondaryImage = !!secondaryImage;
    } else {
      this.hasAdditionalImage = false;
      this.hasSecondaryImage = false;
    }
  }

  private getEdpInfo() {
    const reportRecordData = this.reportRecordDataLocal;
    let entityId = reportRecordData.associatedProperty
      ? reportRecordData.associatedProperty.entity.id
      : reportRecordData.data.entity.id;
    if (PACIFIC_TEMPLATES_IMAGE_ORDER.AVAILABILITY_ORDER.includes(this.currentView().templateId)) {
      entityId = -reportRecordData.data.entity.id;
    }
    return entityId;
  }

  public resetImages() {
    this.reportResetImagesService
      .resetReportAdditionalImages(this.reportRecordDataLocal, this.reportId(), this.currentView().templateId)
      .subscribe(() => {
        this.reportExportViewService.getImages(this.reportId()).subscribe((res: ReportAllRecordsImage) => {
          this.reportImagesService.setReportRecordImages(res);
          this.reportRecordDataLocal = { ...this.reportRecordDataLocal };
          this.cdr.markForCheck();
        });
        // the reason why again reassigning is to re generate the image which is not under the reportRecordData.
        // So when there is edit it was not calling
      });
  }
}
